import React from "react";
import { useSelector } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";

import {
  Create,
  Edit,
  List,
  Filter,
  SimpleForm,
  TextInput,
  SelectInput,
  SelectArrayInput,
  TextField,
  FunctionField,
  required,
  CreateButton,
  EditButton,
  DeleteButton,
  FilterButton
} from "jazasoft";

import Datagrid from "../../components/Datagrid";
import HistoryButton from "../../components/LibraryHistoryButton";

import useUnit from "../../hooks/useUnit";
import { inputOptions, isEmpty, listFromCsv } from "../../utils/helpers";
import { useEntityPrivilege, useSetting } from "../../hooks";
import SettingKeys from "../../utils/SettingKeys";
import { SupplierFields } from "../../utils/entityFields";
import getValidation from "../../utils/getValidation";
import hasPrivilege from "../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../utils/privilege";

export const CreateSupplier = props => {
  const { unitChoices: unitList } = useUnit();
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, props.resource);

  return (
    <Create {...props} parse={({ unitIds, ...record }) => ({ ...record, unitIds: unitIds?.join(",") })}>
      <SimpleForm defaultValue={{ unitIds: unitList?.map(e => e.id) || [] }} redirect="home">
        {unitList?.length > 1 && <SelectArrayInput validate={[required()]} source="unitIds" label="Units" choices={unitList} {...inputOptions(3)} />}
        {fields.includes(SupplierFields.NAME) && (
          <TextInput source="name" validate={getValidation(validationRule[SupplierFields.NAME])} {...inputOptions(3)} />
        )}
        {fields.includes(SupplierFields.DESC) && (
          <TextInput source="desc" validate={getValidation(validationRule[SupplierFields.DESC])} {...inputOptions(3)} />
        )}
      </SimpleForm>
    </Create>
  );
};

export const EditSupplier = props => {
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, props.resource);

  const record = useSelector(
    state =>
      state.jazasoft.resources[props.resource] &&
      state.jazasoft.resources[props.resource].data &&
      state.jazasoft.resources[props.resource].data[props.id]
  );
  const { unitChoices } = useUnit(record);

  return (
    <Edit
      {...props}
      format={({ unitIds, ...record }) => ({ ...record, unitIds: listFromCsv(unitIds) })}
      parse={({ unitIds, ...record }) => ({ ...record, unitIds: unitIds?.join(",") })}>
      <SimpleForm redirect="home" form="record-form-edit">
        {unitChoices?.length > 1 && (
          <SelectArrayInput validate={[required()]} source="unitIds" label="Units" choices={unitChoices} {...inputOptions(3)} />
        )}
        {fields.includes(SupplierFields.NAME) && (
          <TextInput
            source="name"
            validate={getValidation(validationRule[SupplierFields.NAME])}
            options={{ ...inputOptions(3), disabled: record && record.isReferenced }}
          />
        )}
        {fields.includes(SupplierFields.DESC) && (
          <TextInput source="desc" validate={getValidation(validationRule[SupplierFields.NAME])} {...inputOptions(3)} />
        )}
      </SimpleForm>
    </Edit>
  );
};

const useHomeStyle = makeStyles(theme => ({
  buttonEdit: {
    width: theme.spacing(14)
  },
  buttonDelete: {
    width: theme.spacing(16)
  }
}));

export const SupplierHome = props => {
  const { roles, hasAccess } = props;
  const classes = useHomeStyle();
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);

  const { disableCreate, disableEdit, disableDelete } = useEntityPrivilege("supplier");
  const { allUnitList } = useUnit();
  const unitChoices = [{ id: "all", name: "All Units" }, ...allUnitList.map(({ unitId, name }) => ({ id: unitId, name }))];

  return (
    <List
      actions={({ basePath }) => (
        <div>
          {hasPrivilege(roles, hasAccess, ResourceId.SUPPLIER, Scope.WRITE) && !disableCreate && (
            <CreateButton basePath={basePath} showLabel={false} />
          )}
          {allUnitList?.length > 1 && <FilterButton showLabel={false} />}
        </div>
      )}
      filters={
        <Filter>
          <SelectInput source="unitIds" i18nKey={props.i18nKey} choices={unitChoices} {...inputOptions(12)} />
        </Filter>
      }
      parse={({ unitIds, ...rest }) => ({ ...rest, unitIds: unitIds === "all" ? unitIds : !isEmpty(unitIds) ? `*${unitIds}*` : undefined })}
      {...props}>
      <Datagrid classes={{ headerCell: classes.headerCell, rowCell: classes.rowCell }}>
        {fields.includes(SupplierFields.NAME) && <TextField source="name" />}
        {allUnitList.length > 1 && (
          <FunctionField
            source="unitIds"
            label="Units"
            render={record =>
              allUnitList
                .filter(u => listFromCsv(record?.unitIds).includes(u.unitId))
                .map(u => u.name)
                .join(", ")
            }
          />
        )}
        {fields.includes(SupplierFields.DESC) && <TextField source="desc" />}
        <HistoryButton cellClassName={classes.buttonEdit} i18nKey={props.i18nKey} />
        {hasPrivilege(roles, hasAccess, ResourceId.SUPPLIER, Scope.UPDATE) && !disableEdit && <EditButton cellClassName={classes.buttonEdit} />}
        {hasPrivilege(roles, hasAccess, ResourceId.SUPPLIER, Scope.DELETE) && !disableDelete && <DeleteButton cellClassName={classes.buttonDelete} />}
      </Datagrid>
    </List>
  );
};
